import { create } from 'zustand';

type SelectOption = { id: number; label: string; value: string };
type PlantSelectOption = { id: number; label: string; value: string; identifier: string; companyId: number };

interface ConsumerStoreState {
  dateRange: [Date | null, Date | null];
  setDateRange: (dateRange: [Date | null, Date | null]) => void;
  companies: SelectOption[];
  setCompanies: (companies: SelectOption[]) => void;
  cups: PlantSelectOption[];
  setCups: (cups: PlantSelectOption[]) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  shouldLoadData: boolean;
  setShouldLoadData: (should: boolean) => void;
  queryCounter: number;
  incrementQueryCounter: () => void;
  setCompaniesWithCupsCleanup: (
    companies: SelectOption[],
    allCups: Array<{ id: number; identifier: string; alias: string; companyId: number }>
  ) => void;
  averageNomination: number | null;
  setAverageNomination: (value: number | null) => void;
  averageEnagas: number | null;
  setAverageEnagas: (value: number | null) => void;
}

const useConsumerStore = create<ConsumerStoreState>((set, get) => ({
  dateRange: [null, null],
  setDateRange: (dateRange) => set({ dateRange }),
  companies: [],
  setCompanies: (companies) => set({ companies }),
  cups: [],
  setCups: (cups) => set({ cups }),
  averageNomination: null,
  setAverageNomination: (value) => set({ averageNomination: value }),
  averageEnagas: null,
  setAverageEnagas: (value) => set({ averageEnagas: value }),
  isLoading: false,
  setIsLoading: (loading) => set({ isLoading: loading }),
  shouldLoadData: false,
  setShouldLoadData: (should) => set({ shouldLoadData: should }),
  queryCounter: 0,
  incrementQueryCounter: () => set((state) => ({ queryCounter: state.queryCounter + 1 })),
  setCompaniesWithCupsCleanup: (companies, allCups) => {
    const currentCups = get().cups;
    const selectedCompanyIds = companies.map((c) => c.id);

    // Filter out CUPS that don't belong to any of the selected companies
    const validCups = currentCups.filter((selectedCup) => {
      const cupData = allCups.find((cup) => cup.id === selectedCup.id);
      return cupData && selectedCompanyIds.includes(cupData.companyId);
    });

    // Get all available CUPS for the selected companies
    const availableCups = allCups.filter((cup) => selectedCompanyIds.includes(cup.companyId));

    // Auto-select if only one CUPS is available
    const cupsToSet =
      availableCups.length === 1
        ? [
            {
              id: availableCups[0].id,
              label: availableCups[0].alias,
              value: String(availableCups[0].id),
              identifier: availableCups[0].identifier,
              companyId: availableCups[0].companyId,
            },
          ]
        : validCups;

    set({
      companies,
      cups: cupsToSet,
    });
  },
}));

export default useConsumerStore;
